/**
 * Hero Animation Module
 *
 * Smooth entrance animations for the hero section.
 *
 * @package TheRealEstates
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export default class HeroAnimation {
  constructor() {
    this.hero = document.querySelector('[data-hero]');

    if (!this.hero) return;

    this.elements = {
      eyebrow: this.hero.querySelector('[data-hero-eyebrow]'),
      headline: this.hero.querySelector('[data-hero-headline]'),
      subheadline: this.hero.querySelector('[data-hero-subheadline]'),
      cta: this.hero.querySelector('[data-hero-cta]'),
      image: this.hero.querySelector('[data-hero-image].is-active'),
      scrollIndicator: this.hero.querySelector('[data-scroll-indicator]'),
      stats: this.hero.querySelectorAll('[data-hero-stat]'),
    };

    this.init();
  }

  init() {
    this.setInitialStates();
    this.createEntranceTimeline();
    this.animateScrollIndicator();
  }

  setInitialStates() {
    // Text elements - start blurred and offset
    const textElements = [
      this.elements.eyebrow,
      this.elements.headline,
      this.elements.subheadline,
    ].filter(Boolean);

    gsap.set(textElements, {
      opacity: 0,
      y: 25,
      filter: 'blur(8px)'
    });

    // Buttons - disable CSS transitions and add blur for entrance animation
    if (this.elements.cta) {
      const buttons = this.elements.cta.querySelectorAll('.btn');
      gsap.set(buttons.length > 0 ? buttons : this.elements.cta, {
        opacity: 0,
        y: 20,
        filter: 'blur(8px)',
        transition: 'none'
      });
    }

    // Stats - subtle blur
    if (this.elements.stats.length > 0) {
      gsap.set(this.elements.stats, {
        opacity: 0,
        y: 15,
        filter: 'blur(4px)'
      });
    }

    // Scroll indicator
    if (this.elements.scrollIndicator) {
      gsap.set(this.elements.scrollIndicator, { opacity: 0 });
    }

    // Image - already visible via CSS, just add subtle scale
    if (this.elements.image) {
      gsap.set(this.elements.image, { scale: 1.05 });
    }
  }

  createEntranceTimeline() {
    const tl = gsap.timeline({
      defaults: {
        ease: 'power2.out',
      },
    });

    // Image subtle zoom out (runs in background)
    if (this.elements.image) {
      tl.to(this.elements.image, {
        scale: 1,
        duration: 1.8,
        ease: 'power1.out',
      }, 0);
    }

    // Text content - blur in with staggered timing
    if (this.elements.eyebrow) {
      tl.to(this.elements.eyebrow, {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.7,
      }, 0.1);
    }

    if (this.elements.headline) {
      tl.to(this.elements.headline, {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.8,
      }, 0.15);
    }

    if (this.elements.subheadline) {
      tl.to(this.elements.subheadline, {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.7,
      }, 0.3);
    }

    // Buttons - fade in with blur, identical animation for both buttons
    if (this.elements.cta) {
      const buttons = this.elements.cta.querySelectorAll('.btn');
      tl.to(buttons.length > 0 ? buttons : this.elements.cta, {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.6,
        ease: 'power2.out',
        stagger: 0.08,
        onComplete: () => {
          // Restore CSS transitions and clear filter after entrance animation
          gsap.set(buttons, { clearProps: 'transition,filter' });
        }
      }, 0.3);
    }

    // Stats - blur in
    if (this.elements.stats.length > 0) {
      tl.to(this.elements.stats, {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.5,
        stagger: 0.06,
      }, 0.6);
    }

    // Scroll indicator - fade in last
    if (this.elements.scrollIndicator) {
      tl.to(this.elements.scrollIndicator, {
        opacity: 1,
        duration: 0.5,
      }, 0.85);
    }

    return tl;
  }

  animateScrollIndicator() {
    if (!this.elements.scrollIndicator) return;

    // Gentle floating animation (delayed to start after entrance)
    gsap.to(this.elements.scrollIndicator, {
      y: 8,
      duration: 1.8,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
      delay: 1.5,
    });

    // Fade out on scroll (delayed to avoid conflict with entrance animation)
    gsap.delayedCall(1.5, () => {
      ScrollTrigger.create({
        trigger: this.hero,
        start: 'top top',
        end: '25% top',
        scrub: true,
        animation: gsap.to(this.elements.scrollIndicator, {
          opacity: 0,
          ease: 'none',
        }),
      });
    });
  }

  destroy() {
    gsap.killTweensOf([
      this.elements.eyebrow,
      this.elements.headline,
      this.elements.subheadline,
      this.elements.cta,
      this.elements.image,
      this.elements.scrollIndicator,
      ...this.elements.stats,
    ]);
  }
}
